import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, X, Bell } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { nexusApi, useNexusAuth } from "@/context/NexusAuthContext";
import ListingCard from "@/components/nexus/ListingCard";
import { CATEGORIES, REGION_GROUPS, STATUS_META } from "@/pages/nexus/meta";
import useSEO, { canonicalFor } from "@/lib/useSEO";
import "@/pages/nexus/nexus.css";

export default function NexusListings() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { buyer } = useNexusAuth() || {};
  useSEO({
    title: "Marketplace — Corpora Nexus",
    description: "Browse curated regulated & intangible-asset listings: CySEC, MiCA, Mauritius IDL, Seychelles Securities Dealer, Canada MSB. Confidential and NDA-gated.",
    canonical: canonicalFor("/nexus/listings"),
    image: canonicalFor("/brand/nexus-og.jpg"),
  });
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const region_group = params.get("region_group") || "";
  const jurisdiction = params.get("jurisdiction") || "";
  const status = params.get("status") || "";

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (category) qs.set("category", category);
    if (region_group) qs.set("region_group", region_group);
    if (jurisdiction) qs.set("jurisdiction", jurisdiction);
    if (status) qs.set("status", status);
    nexusApi.get(`/listings?${qs.toString()}`)
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .finally(() => setLoading(false));
  }, [q, category, region_group, jurisdiction, status]);

  const setParam = (k, v) => {
    const n = new URLSearchParams(params);
    if (v) n.set(k, v); else n.delete(k);
    setParams(n, { replace: false });
  };

  const jurisdictions = Array.from(new Set(items.map((i) => i.jurisdiction))).sort();
  const hasFilters = category || region_group || jurisdiction || status || q;

  return (
    <div data-testid="nexus-listings">
      {/* Compact header */}
      <section className="nx-hero" style={{ padding: "72px 0 40px" }}>
        <div className="nx-container nx-hero-inner">
          <div className="nx-eyebrow rule mb-6">{t("nexus.listings.eyebrow")}</div>
          <h1 className="nx-h1 mb-6">{t("nexus.listings.heroTitle")}</h1>
          <div className="relative max-w-xl">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--bone-2)]/40" />
            <input
              type="text"
              value={q}
              onChange={(e) => setParam("q", e.target.value)}
              placeholder={t("nexus.listings.searchPlaceholder")}
              className="nx-field pl-11"
              data-testid="listings-search"
            />
          </div>
        </div>
      </section>

      {/* Sticky filter bar */}
      <div
        className="sticky top-[65px] z-20"
        style={{
          background: "rgba(10,11,13,0.85)",
          backdropFilter: "blur(14px)",
          borderBottom: "1px solid var(--line)",
        }}
      >
        <div className="nx-container py-4 flex flex-wrap items-center gap-3" data-testid="listings-filters">
          <select value={category} onChange={(e) => setParam("category", e.target.value)} className="nx-field !w-auto text-xs !py-2" data-testid="filter-category">
            <option value="">{t("nexus.listings.allCategories")}</option>
            {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <select value={region_group} onChange={(e) => setParam("region_group", e.target.value)} className="nx-field !w-auto text-xs !py-2" data-testid="filter-region">
            <option value="">{t("nexus.listings.allRegions")}</option>
            {REGION_GROUPS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
          <select value={jurisdiction} onChange={(e) => setParam("jurisdiction", e.target.value)} className="nx-field !w-auto text-xs !py-2" data-testid="filter-jurisdiction">
            <option value="">{t("nexus.listings.allJurisdictions")}</option>
            {jurisdictions.map((j) => <option key={j} value={j}>{j}</option>)}
          </select>
          <select value={status} onChange={(e) => setParam("status", e.target.value)} className="nx-field !w-auto text-xs !py-2" data-testid="filter-status">
            <option value="">{t("nexus.listings.anyStatus")}</option>
            {Object.entries(STATUS_META).map(([id, m]) => <option key={id} value={id}>{m.label}</option>)}
          </select>
          {hasFilters && (
            <button
              onClick={() => setParams(new URLSearchParams())}
              className="inline-flex items-center gap-1.5 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--bone-2)]/70 hover:text-[var(--bone)]"
              data-testid="filter-clear"
            >
              <X size={12} /> {t("nexus.listings.clear")}
            </button>
          )}
          {hasFilters && (category || region_group || jurisdiction) && (
            <button
              onClick={async () => {
                if (!buyer) {
                  const target = `/nexus/listings${params.toString() ? "?" + params.toString() : ""}`;
                  nav(`/nexus/signin?next=${encodeURIComponent(target)}`);
                  return;
                }
                try {
                  await nexusApi.post("/watchlist", {
                    name: "",
                    category,
                    region_group,
                    jurisdiction,
                    tags: [],
                  });
                  toast.success("Watchlist saved. We'll email you the moment a match goes live.");
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Could not save watchlist");
                }
              }}
              className="inline-flex items-center gap-1.5 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#C9896E] hover:text-[var(--bone)]"
              data-testid="filter-save-watchlist"
              title="Save this search and get instant email alerts"
            >
              <Bell size={12} /> Save search
            </button>
          )}
          <div className="ml-auto font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--bone-2)]/50" data-testid="listings-count">
            {loading ? t("nexus.listings.loading") : t("nexus.listings.listings", { count: total })}
          </div>
        </div>
      </div>

      {/* Results */}
      <section className="nx-container py-14">
        {loading ? (
          <div className="py-16 text-center text-[var(--bone-2)]/50 font-mono text-xs">{t("nexus.listings.loadingMarketplace")}</div>
        ) : items.length === 0 ? (
          <div className="nx-card nx-card-lg text-center">
            <div className="font-serif text-2xl text-[var(--bone)] mb-3">{t("nexus.listings.noMatch")}</div>
            <p className="text-[var(--bone-2)] mb-6 max-w-md mx-auto">{t("nexus.listings.setCriteria")}</p>
            <button onClick={() => setParams(new URLSearchParams())} className="nx-btn nx-btn-secondary">{t("nexus.listings.resetFilters")}</button>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {items.map((l, i) => (
              <motion.div
                key={l.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: (i % 6) * 0.05 }}
              >
                <ListingCard listing={l} />
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
